import { IContainer, ILazyBinder, injectContainer } from './container';
import { IProgressPromise } from './promise';

export interface IEnv {
    getValue: <T>(envName: string) => T | null;
    getValueWithDefault: <T>(envName: string, defaultValue: T) => T;
    getNumber: (name: string, defaultValue?: number) => number;
    getBoolean: (name: string, defaultValue?: boolean) => boolean;
    getString: (name: string, defaultValue?: string) => string;
}

export interface IFetchHeader {
    name: string;
    value: string;
}

export type Method = 'GET' | 'PUT' | 'PATCH' | 'POST' | 'DELETE' | 'OPTIONS';
export type ResponseTypes = 'json' | 'text' | 'blob';

export interface IFetchRequest {
    url: string;
    method?: Method;
    headers?: Array<IFetchHeader>;
    body?: string;
    expectedResponseType?: ResponseTypes;
    expectedResponseSize?: number;
}

export interface IFetchResponse<T> {
    request: IFetchRequest;
    statusCode: number;
    isStatusOk: boolean;
    error?: Error;
    payload: T | null;
}

export type IFetcher = <T>(
    requestPayload: IFetchRequest,
    timeout?: number,
) => IProgressPromise<IFetchResponse<T>>;

export interface ILog {
    milestone: (msg: any) => void;
    verbose: (msg: any) => void;
    trace: (msg: any) => void;
    info: (msg: any) => void;
    warn: (msg: any) => void;
    warning: (msg: any) => void;
    error: (msg: any) => void;
    fatal: (msg: any) => void;
}

export interface IQueryURL {
    parse: (queryString: string) => {};
    assemble: (queryArgs: {}) => string;
}

interface IUnzipEntry {
    versionMadeBy: number;
    versionNeededToExtract: number;
    generalPurposeBitFlag: number;
    compressionMethod: number;
    lastModTime: number;
    lastModDate: number;
    crc32: number;
    compressedSize: number;
    uncompressedSize: number;
    internalAttributes: number;
    externalAttributes: number;
    localHeaderOffset: number;
    name: string;
    commment: string;
    extraFields: object[];
}

type IUnzipReadEntriesCallback = (err: Error, entries: IUnzipEntry[]) => void;
type IUnzipReadEntryDataCallback = (err: Error, readStream: IZipReadStream) => void;

interface IUnzipFile {
    readEntries: (callback: IUnzipReadEntriesCallback) => void;
    readEntryData: (
        entry: IUnzipEntry,
        checkCrc: boolean,
        callback: IUnzipReadEntryDataCallback,
    ) => void;
}

type IReadStreamCallback = (value: any) => void;
type IReadStreamEvent = 'data' | 'error' | 'end';

interface IZipReadStream {
    on: (event: IReadStreamEvent, callback: IReadStreamCallback) => void;
}

export interface IZip {
    unzipFile: <T>(blobData: Blob, fileName: string) => Promise<T>;
    unzipAll: <T>(blobData: Blob, destDirectoryPrefix: string) => Promise<{ [keyof: string]: T }>;
    listings: (blobData: Blob) => Promise<Array<string>>;
}

export interface IKVStorage {
    getItem: (key: any) => Promise<any>;
    setItem: (key: any, value: any) => Promise<void>;
    removeItem: (key: any) => Promise<void>;
    clear: () => Promise<void>;
    length: () => Promise<any>;
}

export enum IBrowserPlatform {
    Unknown = 0,
    MobileiOS = 1,
    MobileAndroid = 2,
}

export interface IBrowser {
    platform: IBrowserPlatform;
    isMobile: boolean;
    isInstalled: boolean;
}

export interface ICache {
    add: (key: string | symbol, value: any) => void;
    get: (key: string | symbol) => any;
}

export interface ICacheController {
    getAssociatedCache: (associatedObject: any) => ICache | null;
    getGlobalCache: () => ICache;
}

export type LANGUAGE_CODE =
    | 'cs'
    | 'de'
    | 'en'
    | 'es'
    | 'fr'
    | 'it'
    | 'ja'
    | 'ko'
    | 'pl'
    | 'pt'
    | 'ru'
    | 'zh'
    | 'nl'
    | 'ar'
    | 'bp'
    | 'ga'
    | 'gd';

export interface ITranslationStorage {
    translation: (
        language: LANGUAGE_CODE,
        key: string,
        parameters?: Array<string>,
        fallback?: string,
    ) => string;
}

const TYPES = {
    IEnv: Symbol.for('IEnv'),
    IFetch: Symbol.for('IFetch'),
    ILog: Symbol.for('ILog'),
    IQueryURL: Symbol.for('IQueryURL'),
    IZip: Symbol.for('IZip'),
    IKVStorage: Symbol.for('IKVStorage'),
    IBrowser: Symbol.for('IBrowser'),
    ICache: Symbol.for('ICache'),
    ICacheController: Symbol.for('ICacheController'),
    ITranslationStorage: Symbol.for('ITranslationStorage'),
};

export function injectENV(container: IContainer = injectContainer()): IEnv {
    return container.get<IEnv>(TYPES.IEnv);
}

export function bindENV(val: IEnv, container: IContainer = injectContainer()) {
    return container.bind<IEnv>(TYPES.IEnv, val);
}

export function injectFetch(container: IContainer = injectContainer()): Promise<IFetcher> {
    return container.get<ILazyBinder<IFetcher>>(TYPES.IFetch)();
}

export function bindFetch(val: ILazyBinder<IFetcher>, container: IContainer = injectContainer()) {
    return container.bind<ILazyBinder<IFetcher>>(TYPES.IFetch, val);
}

export function injectLog(container: IContainer = injectContainer()): ILog {
    return container.get<ILog>(TYPES.ILog);
}

export function bindLog(val: ILog, container: IContainer = injectContainer()) {
    return container.bind<ILog>(TYPES.ILog, val);
}

export function injectQueryURL(container: IContainer = injectContainer()): IQueryURL {
    return container.get<IQueryURL>(TYPES.IQueryURL);
}

export function bindQueryURL(val: IQueryURL, container: IContainer = injectContainer()) {
    return container.bind<IQueryURL>(TYPES.IQueryURL, val);
}

export function injectZip(container: IContainer = injectContainer()): Promise<IZip> {
    return container.get<ILazyBinder<IZip>>(TYPES.IZip)();
}

export function bindZip(val: ILazyBinder<IZip>, container: IContainer = injectContainer()) {
    return container.bind<ILazyBinder<IZip>>(TYPES.IZip, val);
}

export function injectKVStorage(container: IContainer = injectContainer()): Promise<IKVStorage> {
    return container.get<ILazyBinder<IKVStorage>>(TYPES.IKVStorage)();
}

export function bindKVStorage(
    val: ILazyBinder<IKVStorage>,
    container: IContainer = injectContainer(),
) {
    return container.bind<ILazyBinder<IKVStorage>>(TYPES.IKVStorage, val);
}

export function injectBrowser(container: IContainer = injectContainer()): Promise<IBrowser> {
    return container.get<ILazyBinder<IBrowser>>(TYPES.IBrowser)();
}

export function bindBrowser(val: ILazyBinder<IBrowser>, container: IContainer = injectContainer()) {
    return container.bind<ILazyBinder<IBrowser>>(TYPES.IBrowser, val);
}

export function injectCache(container: IContainer = injectContainer()): ICache {
    return container.get<ICache>(TYPES.ICache);
}

export function bindCache(val: ICache, container: IContainer = injectContainer()) {
    return container.bind<ICache>(TYPES.ICache, val);
}

export function injectCacheController(container: IContainer = injectContainer()): ICacheController {
    return container.get<ICacheController>(TYPES.ICacheController);
}

export function bindCacheController(
    val: ICacheController,
    container: IContainer = injectContainer(),
) {
    return container.bind<ICacheController>(TYPES.ICacheController, val);
}

export function injectTranslationStorage(
    container: IContainer = injectContainer(),
): Promise<ITranslationStorage> {
    return container.get<ILazyBinder<ITranslationStorage>>(TYPES.ITranslationStorage)();
}

export function bindTranslationStorage(
    val: ILazyBinder<ITranslationStorage>,
    container: IContainer = injectContainer(),
) {
    return container.bind<ILazyBinder<ITranslationStorage>>(TYPES.ITranslationStorage, val);
}
